// AdvancedPatternsDetail.jsx
// LEFT TOC + scroll-spy + MAIN content + RIGHT Sponsored
// ✅ More detailed + more readable (Template + Example for every section)
// ✅ ONE consistent example across the page: “Facebook-like Home Feed”
// ✅ Bootstrap-friendly, React Router v4/v5 compatible
// ✅ Safe AdSense for SPA navigation
// ✅ Clear “What / Why / How / Tradeoffs / Pitfalls / Template / Example / Interview line” structure

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ====================================================================== */
/*  AdSense                                                               */
/* ====================================================================== */

const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

/**
 * Loads AdSense script once, then re-pushes ads on SPA route transitions.
 * Safe to call per page.
 */
function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const script = document.createElement("script");
      script.setAttribute("data-adsbygoogle", "true");
      script.async = true;
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      script.crossOrigin = "anonymous";
      document.head.appendChild(script);
      return;
    }

    // If script already present, try to refresh ad slots (ignore failures)
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [clientId, enabled]);
}

function AdSlot({
  enabled = true,
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  className = "",
  style = { display: "block", minHeight: 90 },
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

/* ====================================================================== */
/*  Small UI helpers                                                       */
/* ====================================================================== */

function Badge({ tone = "secondary", children }) {
  return (
    <span className={`badge bg-${tone}`} style={{ whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function Card({ children, className = "" }) {
  return <div className={`card border-0 shadow-sm ${className}`}>{children}</div>;
}

function CardBody({ children, className = "" }) {
  return <div className={`card-body ${className}`}>{children}</div>;
}

function TitleRow({ left, right }) {
  return (
    <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
      <div className="fw-semibold">{left}</div>
      {right}
    </div>
  );
}

function Muted({ children }) {
  return (
    <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
      {children}
    </div>
  );
}

function Divider() {
  return <hr className="my-3" />;
}

function Bullets({ items }) {
  return (
    <ul className="mb-0">
      {items.map((x) => (
        <li key={x}>{x}</li>
      ))}
    </ul>
  );
}

function Pitfalls({ items }) {
  return (
    <div className="alert alert-warning small mt-3 mb-0">
      <div className="fw-semibold mb-1">Common pitfalls</div>
      <ul className="mb-0">
        {items.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * “Template” block = your reusable interview-ready checklist.
 */
function TemplateBlock({ title = "Template", children }) {
  return (
    <Card className="mt-3">
      <CardBody>
        <TitleRow left={title} right={<Badge tone="secondary">Template</Badge>} />
        <Muted>{children}</Muted>
      </CardBody>
    </Card>
  );
}

/**
 * “Example” block = apply template to the ONE consistent system (Home Feed).
 */
function ExampleBlock({ title = "Example", children }) {
  return (
    <Card className="mt-3">
      <CardBody>
        <TitleRow left={title} right={<Badge tone="success">Example</Badge>} />
        <Muted>{children}</Muted>
      </CardBody>
    </Card>
  );
}

function CodeBlock({ title = "Snippet", children }) {
  return (
    <Card className="mt-3">
      <CardBody>
        <TitleRow left={title} right={<Badge tone="secondary">Template</Badge>} />
        <pre
          className="bg-light rounded p-3 border small mb-0"
          style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
        >
          <code>{children}</code>
        </pre>
      </CardBody>
    </Card>
  );
}

function InterviewLine({ children }) {
  return (
    <div className="alert alert-primary small mt-3 mb-0">
      <div className="fw-semibold mb-1">Interview one-liner</div>
      <div style={{ whiteSpace: "pre-wrap" }}>{children}</div>
    </div>
  );
}

/**
 * Section wrapper:
 * - consistent heading styles
 * - optional subtitle
 * - “Back to top”
 */
function Section({ id, title, subtitle, children }) {
  return (
    <section id={id} className="mb-5">
      <div className="mb-2">
        <h2 className="h4 mb-1">{title}</h2>
        {subtitle ? <div className="text-muted">{subtitle}</div> : null}
      </div>

      {children}

      <div className="mt-3">
        <a href="#top" className="small text-muted">
          ↑ Back to top
        </a>
      </div>
    </section>
  );
}

/* ====================================================================== */
/*  TOC                                                                    */
/* ====================================================================== */

const TOC = [
  { id: "cqrs-es", label: "A) CQRS & Event Sourcing" },
  { id: "sagas", label: "B) Sagas: orchestration vs choreography" },
  { id: "search", label: "C) Search systems & relevancy" },
  { id: "feeds", label: "D) Feeds: fan-out vs fan-in" },
  { id: "geo", label: "E) Geo & mapping (H3/Geohash)" },
  { id: "media", label: "F) Media delivery & ABR/CDN" },
  { id: "payments", label: "G) Payments, ledger, finops" },
  { id: "feature-store", label: "H) Feature stores & realtime ML" },
  { id: "glue", label: "Glue patterns (how these combine)" },
  { id: "bites", label: "Quick sound bites" },
];

/* ====================================================================== */
/*  RIGHT rail                                                              */
/* ====================================================================== */

function RightSponsoredCard() {
  return (
    <Card>
      <CardBody>
        <h3 className="h6 text-uppercase text-muted mb-2">Sponsored</h3>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </CardBody>
    </Card>
  );
}

/* ====================================================================== */
/*  Page                                                                    */
/* ====================================================================== */

export default function AdvancedPatternsDetail() {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const [activeId, setActiveId] = useState(TOC[0].id);

  /**
   * ONE example across the entire page.
   * Keep it stable so every section can reference it consistently.
   */
  const homeFeed = useMemo(
    () => ({
      name: "Facebook-like Home Feed",
      users: "hundreds of millions",
      traffic: "high read QPS, bursty writes, viral spikes",
      readFlow:
        "GET /feed → fetch candidate postIds → hydrate posts/media → rank → apply rules → return cursor",
      writeFlow:
        "POST /post → validate → store → fan-out or author timeline → async pipelines (indexing, counters, notifications)",
      keyServices: [
        "API Gateway + AuthN/AuthZ",
        "Social Graph Service (follow edges)",
        "Timeline Store (Redis/Cassandra)",
        "Posts Service (metadata + permissions)",
        "Media Service + Transcode Pipeline + CDN",
        "Ranking Service (online scoring)",
        "Feature Store (online features)",
        "Stream/Event Bus (Kafka/Kinesis)",
        "Search Index (Elastic)",
        "Counters/Engagement (likes/comments/views)",
      ],
    }),
    []
  );

  /* -------------------- Scroll spy -------------------- */
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px 0px -60% 0px",
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setActiveId(entry.target.id);
      });
    }, observerOptions);

    TOC.forEach(({ id: sectionId }) => {
      const el = document.getElementById(sectionId);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="container-lg py-4" id="top">
      {/* Breadcrumb */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>
        <span className="text-muted"> / </span>
        <span>Advanced patterns</span>
      </nav>

      {/* Header */}
      <header
        className="mb-4 rounded-3 p-3 p-md-4 text-center"
        style={{
          background:
            "linear-gradient(135deg, rgba(13,110,253,.06), rgba(214,51,132,.07))",
        }}
      >
        <div>
          <span className="badge bg-dark text-white mb-2">System Design</span>
          <h1 className="h3 mb-1">13) Advanced patterns — detailed guide</h1>
          <p className="text-muted mb-3">
            CQRS &amp; Event Sourcing, sagas, search, feeds, geo, media,
            payments/finops, and feature stores—explained using one consistent
            system you can describe end-to-end.
          </p>

          <div className="d-flex flex-wrap justify-content-center gap-2">
            <Badge tone="primary">{homeFeed.name}</Badge>
            <Badge tone="light" className="text-dark border">
              Template + Example per section
            </Badge>
            <Badge tone="light" className="text-dark border">
              Interview-ready one-liners
            </Badge>
          </div>
        </div>
      </header>

      {/* Layout: TOC + MAIN + RIGHT */}
      <div className="row g-4">
        {/* LEFT TOC */}
        <aside className="col-12 col-xl-2 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <Card>
              <CardBody>
                <h3 className="h6 text-uppercase text-muted mb-2">On this page</h3>
                <nav className="nav flex-column small">
                  {TOC.map(({ id: sectionId, label }) => (
                    <a
                      key={sectionId}
                      href={`#${sectionId}`}
                      className={`nav-link px-0 ${
                        activeId === sectionId ? "fw-semibold text-primary" : ""
                      }`}
                      aria-current={activeId === sectionId ? "true" : undefined}
                    >
                      {label}
                    </a>
                  ))}
                </nav>
              </CardBody>
            </Card>

            {/* Example context (visible on XL only) */}
            <Card className="mt-3 d-none d-xl-block">
              <CardBody className="small">
                <div className="fw-semibold mb-2">Example context</div>

                <div className="mb-2">
                  <Badge tone="primary">{homeFeed.name}</Badge>
                </div>

                <div className="text-muted mb-2">
                  <div className="fw-semibold">Read path</div>
                  {homeFeed.readFlow}
                </div>

                <div className="text-muted">
                  <div className="fw-semibold">Write path</div>
                  {homeFeed.writeFlow}
                </div>

                <Divider />

                <div className="fw-semibold mb-1">Typical components</div>
                <ul className="mb-0 text-muted">
                  {homeFeed.keyServices.slice(0, 6).map((x) => (
                    <li key={x}>{x}</li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7">
          {/* A) CQRS & Event Sourcing */}
          <Section
            id="cqrs-es"
            title="A) CQRS & Event Sourcing"
            subtitle="Use this when you need correct writes + many fast read views + the ability to rebuild safely."
          >
            <div className="row g-3">
              <div className="col-12 col-lg-6">
                <Card>
                  <CardBody>
                    <TitleRow left="What" right={<Badge>Concept</Badge>} />
                    <Bullets
                      items={[
                        "CQRS: separate write model (commands) from read model (queries).",
                        "Event Sourcing: source of truth is an append-only event log (immutable events).",
                        "Read models are projections built from events (Redis/Cassandra/Elastic).",
                      ]}
                    />
                  </CardBody>
                </Card>
              </div>

              <div className="col-12 col-lg-6">
                <Card>
                  <CardBody>
                    <TitleRow left="Why" right={<Badge>Motivation</Badge>} />
                    <Bullets
                      items={[
                        "Writes enforce invariants once; reads are optimized per use-case.",
                        "Independent scaling: write throughput vs read latency.",
                        "Auditability (who did what when) + replay/backfill without risky DB scripts.",
                      ]}
                    />
                  </CardBody>
                </Card>
              </div>
            </div>

            <TemplateBlock title="Template — how to present CQRS/ES in interviews">
{`1) Define the invariants on the write side
   - authorization, state transitions, limits, uniqueness, ordering

2) Define the event model (append-only)
   - examples: PostCreated, PostDeleted, FollowCreated, LikeAdded
   - rules: event schema is “forever” → versioning/upcasters

3) Choose the event log / stream
   - Kafka/Kinesis/EventStore
   - partition strategy (postId or userId) + ordering guarantees

4) Build projections (read models)
   - timeline store (IDs), post view store (metadata), search index (text)
   - each projection has its own schema optimized for its query

5) Define consistency + freshness SLA
   - “post visible in feed within 30s p95”
   - what happens if projection lags (degrade gracefully)

6) Avoid dual-writes
   - use Outbox pattern or an event store that is the database

7) Rebuild strategy
   - drop projection → replay events → rebuild → verify → swap`}
            </TemplateBlock>

            <ExampleBlock title={`${homeFeed.name} — CQRS/ES applied`}>
{`Write path (commands):
• POST /post
  - validate (auth, limits, spam checks)
  - append PostCreated(postId, authorId, contentRef, ts, visibility)
  - publish to stream (or write to outbox then publish)

Event log:
• topic: posts-events (partition by authorId or postId)

Projections:
1) UserTimelineProjection
   - consumes PostCreated + FollowCreated
   - builds “timeline IDs” per user (fast read)
2) PostViewProjection
   - stores post metadata by postId (fast hydrate)
3) SearchProjection
   - indexes post text/tags into Elasticsearch
4) EngagementProjection
   - counts likes/comments/views (for ranking features)

Read path:
• GET /feed
  - fetch postIds (timeline)
  - batch-hydrate PostViewProjection
  - rank + rules
  - return cursor

Rebuild:
• if ranking features change, replay events to rebuild projections safely.`}
            </ExampleBlock>

            <Pitfalls
              items={[
                "Dual write (DB + stream) without outbox → mismatched truth",
                "No event versioning plan → stuck when schema changes",
                "Not defining freshness SLA → product confusion",
                "Replay takes too long → need compaction/snapshots/partitioning",
              ]}
            />

            <InterviewLine>
              {`“We keep writes correct by appending immutable events, then build multiple read-optimized projections; if a read model is wrong, we replay safely instead of risky backfills.”`}
            </InterviewLine>
          </Section>

          {/* B) Sagas */}
          <Section
            id="sagas"
            title="B) Sagas: orchestration vs choreography"
            subtitle="Use sagas for multi-step business flows with retries, timeouts, and compensations."
          >
            <div className="row g-3">
              <div className="col-12 col-lg-6">
                <Card>
                  <CardBody>
                    <TitleRow left="What" right={<Badge>Concept</Badge>} />
                    <Bullets
                      items={[
                        "Saga = distributed transaction implemented as steps + compensations.",
                        "Choreography: services react to events and emit next events.",
                        "Orchestration: a workflow engine drives the sequence, retries, and timeouts.",
                      ]}
                    />
                  </CardBody>
                </Card>
              </div>

              <div className="col-12 col-lg-6">
                <Card>
                  <CardBody>
                    <TitleRow left="When to use orchestration" right={<Badge>Decision</Badge>} />
                    <Bullets
                      items={[
                        "Long-running flows (minutes → days).",
                        "You need visibility: where is the workflow stuck?",
                        "Complex compensations + global deadlines.",
                        "Exactly-once activities with automatic retries.",
                      ]}
                    />
                  </CardBody>
                </Card>
              </div>
            </div>

            <TemplateBlock title="Template — saga design checklist">
{`1) Identify the workflow goal
   - “Publish post with media safely and make it visible in feed”

2) Break into steps (each must be idempotent)
   - step inputs/outputs clearly defined

3) Define compensations per step
   - what does “undo” mean if step 3 fails?

4) Add timeouts & retry policy
   - per-step timeout + overall deadline

5) Store saga state
   - workflow engine state store OR your own (dedupe keys)

6) Observability
   - step metrics, traces, dead-letter queue, operator runbook`}
            </TemplateBlock>

            <ExampleBlock title={`${homeFeed.name} — “Create post with video” saga`}>
{`Goal: user posts a video; it becomes visible only if processing & safety checks pass.

Orchestrated steps:
1) CreateDraftPost (writes PostDraftCreated)
2) UploadVideo (multipart upload)
3) TranscodeRenditions (240p/480p/720p)
4) SafetyScan (policy/moderation)
5) PublishPost (writes PostPublished)
6) FanoutToFollowers (async)

Compensations:
• If transcode fails → keep draft hidden + retry
• If safety fails → mark post blocked + delete media reference (or quarantine)

Why orchestration:
• Long-running transcode + explicit timeouts + operator visibility.`}
            </ExampleBlock>

            <Pitfalls
              items={[
                "Steps are not idempotent → duplicates during retries",
                "No compensation strategy → stuck partial state",
                "Hidden coupling in choreography at scale",
                "No global deadline → workflows run forever",
              ]}
            />

            <InterviewLine>
              {`“We choreograph simple event chains, but anything with long timeouts or compensations goes into an orchestrator so we can observe every step and retry safely.”`}
            </InterviewLine>
          </Section>

          {/* Optional in-article ad */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot
                enabled={ADS_ENABLED}
                adSlot="5201219465"
                style={{ display: "block", minHeight: 90, textAlign: "center" }}
              />
            </div>
          )}

          {/* C) Search */}
          <Section
            id="search"
            title="C) Search systems & relevancy"
            subtitle="Full-text search is about indexing + retrieval + ranking, not just storing text."
          >
            <div className="row g-3">
              <div className="col-12 col-lg-6">
                <Card>
                  <CardBody>
                    <TitleRow left="Core pieces" right={<Badge>Concept</Badge>} />
                    <Bullets
                      items={[
                        "Inverted index: term → posting list (fast retrieval).",
                        "Analyzer pipeline: normalize/tokenize/stem/synonyms.",
                        "Ranking: BM25 + recency + personalization + business rules.",
                      ]}
                    />
                  </CardBody>
                </Card>
              </div>

              <div className="col-12 col-lg-6">
                <Card>
                  <CardBody>
                    <TitleRow left="Operational concerns" right={<Badge>Ops</Badge>} />
                    <Bullets
                      items={[
                        "Shards + replicas; merge pressure and disk usage.",
                        "Indexing lag SLA: how long until new content is searchable?",
                        "Guard expensive aggregations and high-cardinality fields.",
                      ]}
                    />
                  </CardBody>
                </Card>
              </div>
            </div>

            <TemplateBlock title="Template — search architecture you can draw">
{`Indexing pipeline:
1) ingest documents (posts/users/pages)
2) normalize + analyze (language aware)
3) enrich (entities, synonyms, spam signals)
4) index into shards (replicate for read)

Query pipeline:
1) parse query (autocomplete/spell/corrections)
2) retrieve candidates (inverted index)
3) rank (BM25 + signals)
4) filter/facets
5) return results + metrics

Define SLAs:
• indexing lag (p95)
• query latency (p95/p99)
• relevance metrics (CTR, zero-result rate)`}
            </TemplateBlock>

            <ExampleBlock title={`${homeFeed.name} — searching posts and people`}>
{`Indexing:
• PostPublished events → SearchProjector → Elasticsearch
• Fields: text, hashtags, authorId, language, created_at, engagement_score

Query “travel nepal”:
1) retrieve candidates via inverted index
2) rank by BM25 + recency + engagement + personalization
3) apply filters (language, friends-only content if required)

Operational contract:
• indexing lag SLA = 30–60s
• monitor: zero-result rate, p95 latency, top failed queries`}
            </ExampleBlock>

            <Pitfalls
              items={[
                "Analyzer mismatch (index vs query) → poor results",
                "Unbounded dynamic fields → mapping explosion",
                "Heavy aggs on high-cardinality fields → cluster instability",
                "No indexing-lag SLA → users can’t find new posts",
              ]}
            />

            <InterviewLine>
              {`“Search is an index + ranking system: we index asynchronously from events, rank with BM25 + recency + personalization, and we publish an indexing-lag SLA.”`}
            </InterviewLine>
          </Section>

          {/* D) Feeds */}
          <Section
            id="feeds"
            title="D) Home feeds: fan-out vs fan-in"
            subtitle="The classic: fast reads vs write amplification. Hybrid is the common real-world answer."
          >
            <div className="row g-3">
              <div className="col-12 col-lg-6">
                <Card>
                  <CardBody>
                    <TitleRow left="Fan-out on write" right={<Badge>Push</Badge>} />
                    <Bullets
                      items={[
                        "On publish: push postId into each follower’s timeline.",
                        "Pros: fast reads; simple read path.",
                        "Cons: expensive writes; celebrity problem.",
                      ]}
                    />
                  </CardBody>
                </Card>
              </div>

              <div className="col-12 col-lg-6">
                <Card>
                  <CardBody>
                    <TitleRow left="Fan-in on read" right={<Badge>Pull</Badge>} />
                    <Bullets
                      items={[
                        "On read: merge followees’ timelines at request time.",
                        "Pros: cheap writes; handles celebs naturally.",
                        "Cons: heavy reads; merge+rank cost; caching required.",
                      ]}
                    />
                  </CardBody>
                </Card>
              </div>
            </div>

            <TemplateBlock title="Template — feed design (hybrid) + ranking pipeline">
{`1) Choose feed storage model
   - fan-out, fan-in, or hybrid

2) Define candidate generation
   - timeline IDs from Redis/Cassandra
   - special handling for celebrity authors

3) Hydration strategy
   - batch fetch posts/media
   - avoid N+1 calls

4) Ranking pipeline
   - candidates → features → score → post-ranking rules
   - enforce safety + diversity + freshness

5) Caching strategy
   - cache feed head (first N IDs)
   - refresh-ahead / background recompute

6) Protection mechanisms
   - rate limits, backpressure workers, hot-key mitigation`}
            </TemplateBlock>

            <ExampleBlock title={`${homeFeed.name} — practical hybrid feed`}>
{`Write:
• Typical users → fan-out into follower timelines (async workers)
• Celebrity users → do NOT fan-out to millions; keep in author timeline

Read (GET /feed):
1) fetch cached “feed head” IDs for user
2) merge in celebrity timelines (fan-in) if needed
3) batch-hydrate post metadata + media refs
4) rank with features (affinity, engagement, recency)
5) apply rules (no duplicates, diversity, safety)
6) return items + cursor

Hot key controls:
• worker queues bounded + backpressure
• per-celebrity cache of recent posts
• cache stampede protection on popular feeds`}
            </ExampleBlock>

            <Pitfalls
              items={[
                "Celebrity fan-out storms (write amplification)",
                "N+1 hydration (one call per post)",
                "Cache stampedes during viral spikes",
                "Online ranking too expensive (move heavy compute offline)",
              ]}
            />

            <InterviewLine>
              {`“We use a hybrid feed: fan-out for typical users, fan-in for celebrities, cache the feed head, and rank via a lightweight online model plus rules.”`}
            </InterviewLine>
          </Section>

          {/* E) Geo */}
          <Section
            id="geo"
            title="E) Geo & mapping (H3/Geohash)"
            subtitle="Spatial indexing for ‘nearby’ experiences, geo-sharding, and heatmaps."
          >
            <TemplateBlock title="Template — geo indexing & radius query">
{`Store:
• compute cellId = H3(lat, lon, resolution)
• store by cellId prefix (locality)

Query radius:
1) compute covering cells (ring/cover)
2) fetch candidates by cellId prefix
3) precise filter using Haversine distance
4) sort/rank by distance and relevance

Scale:
• shard by region/cell prefix + hash inside region
• use multi-resolution for small vs large radii`}
            </TemplateBlock>

            <ExampleBlock title={`${homeFeed.name} — “Events near you” module`}>
{`Use-case:
• Show nearby events/posts in the home feed

Storage:
• event location → H3(r8) cellId

Query:
• user location → k-ring cells → fetch candidate event IDs
• Haversine filter → rank (distance + popularity + personalization)

Scaling:
• shard by H3 prefix to avoid global scans`}
            </ExampleBlock>

            <Pitfalls
              items={[
                "Huge radius creates too many cells (needs caps)",
                "Edge effects near cell borders (cover strategy matters)",
                "Dense metros skew load (need balancing)",
              ]}
            />
          </Section>

          {/* F) Media */}
          <Section
            id="media"
            title="F) Media delivery (ABR + CDN + origin shielding)"
            subtitle="How to serve billions of images/videos reliably and cheaply."
          >
            <TemplateBlock title="Template — media pipeline + delivery checklist">
{`Upload:
1) multipart upload to object store
2) transcode renditions (ABR)
3) publish manifests + segments

Delivery:
• CDN caches versioned URLs (long TTL)
• origin shield reduces origin hits
• signed URLs for private content
• range requests for video seeking

Measure:
• CDN hit rate, startup time, rebuffer %, origin errors, transcode backlog`}
            </TemplateBlock>

            <ExampleBlock title={`${homeFeed.name} — autoplay videos in feed`}>
{`Pipeline:
• upload → transcode → HLS playlist + 4–6s segments
• store segments with versioned URLs (cache safe)

Playback:
• ABR chooses bitrate per bandwidth
• CDN + shield handles viral spikes without crushing origin

Cost:
• monitor egress spikes and low cache hit rate, pre-warm only truly hot videos`}
            </ExampleBlock>

            <Pitfalls
              items={[
                "Bad cache keys (low CDN hit rate)",
                "Missing CORS/range breaks streaming/seeking",
                "Segment duration extremes harm QoE",
              ]}
            />
          </Section>

          {/* G) Payments */}
          <Section
            id="payments"
            title="G) Payments, ledger, finops"
            subtitle="Correct money movement requires idempotency + immutable ledger + reconciliation."
          >
            <TemplateBlock title="Template — payments correctness story">
{`1) Make charges/refunds idempotent
   - Idempotency-Key + unique constraints

2) Record effects in immutable double-entry ledger
   - debit/credit lines are append-only

3) Materialize balances for reads
   - do not mutate ledger rows

4) Reconcile
   - nightly compare ledger totals to PSP/bank reports
   - exceptions queue + runbooks

5) FinOps hooks
   - tag costs, budgets, anomaly detection (egress, hot shards)`}
            </TemplateBlock>

            <ExampleBlock title={`${homeFeed.name} — ads billing + ledger`}>
{`Ads billing:
• impressions/clicks → aggregate → charge advertiser (idempotent)

Ledger:
• debit advertiser / credit platform revenue
• balances are materialized views

Recon:
• nightly compare to payment provider reports
• mismatches go to ops queue

FinOps:
• track major costs: CDN egress, storage, ML inference
• alert on sudden egress spikes during viral events`}
            </ExampleBlock>

            <Pitfalls
              items={[
                "Mutable ledger rows (audit breaks)",
                "No idempotency → duplicate charges",
                "Recon exists but not operationalized (no review)",
              ]}
            />
          </Section>

          {/* H) Feature stores */}
          <Section
            id="feature-store"
            title="H) Feature stores & realtime ML"
            subtitle="Make online ranking consistent with training and safe to roll out."
          >
            <TemplateBlock title="Template — feature store + inference + safety">
{`Definitions:
• Feature store = registry + offline store + online store
• Goal: same features in training and serving, with freshness SLAs

Flow:
1) offline compute (warehouse/lake)
2) online serve (KV/Redis) with TTL + fast lookup
3) online inference: candidates → feature fetch → score → rules

Safety:
• shadow traffic, canary cohorts, rollback switch
• drift monitoring (input/output), latency SLO monitoring`}
            </TemplateBlock>

            <ExampleBlock title={`${homeFeed.name} — ranking service with feature store`}>
{`Ranking:
• candidates → fetch features (affinity, engagement, freshness, author quality)
• online KV returns < 10ms p95
• model score + rule layer

Freshness:
• some features near-real-time (recent interactions)
• others daily batch (author quality)

Rollout:
• shadow 5% traffic to new model
• canary 1% cohort
• rollback if CTR drops or latency breaches`}
            </ExampleBlock>

            <Pitfalls
              items={[
                "Training/serving skew (different feature logic)",
                "Stale features (no TTL or freshness SLAs)",
                "No rollback (bad model hurts metrics)",
              ]}
            />
          </Section>

          {/* Glue patterns */}
          <Section
            id="glue"
            title="Glue patterns (how these combine)"
            subtitle="The interviewer loves when you connect patterns into one coherent story."
          >
            <TemplateBlock title="Template — “glue story” you can say in 30 seconds">
{`Structure your explanation like this:
1) Source of truth (events or DB)
2) Derived views (projections for fast reads)
3) Async pipelines (fan-out, indexing, counters)
4) Serving layer (caches/CDN, pagination)
5) Ranking layer (features + model + rules)
6) Safety/ops (SLAs, retries, monitoring, rollback)

One-line summary:
• “Writes are correct, reads are fast, pipelines are async, and rollouts are safe.”`}
            </TemplateBlock>

            <ExampleBlock title={`${homeFeed.name} — glue story in a clean sequence`}>
{`1) Post/like/follow events are appended (audit + replay).
2) Projections build timelines, post views, search index, and counters.
3) Feed read uses timeline IDs → hydrate → rank → rules.
4) Celebrities handled via fan-in; typical users via fan-out.
5) Media served via CDN with ABR; viral spikes protected by caches/shields.
6) Ranking uses feature store and safe rollout (shadow/canary + rollback).`}
            </ExampleBlock>
          </Section>

          {/* Sound bites */}
          <Section
            id="bites"
            title="Quick sound bites"
            subtitle="Short lines you can memorize and reuse in any system design interview."
          >
            <CodeBlock title="Sound bites (copy/paste)">
{`CQRS/ES:     Writes append events; reads are projections; replay fixes bad projections safely.
Sagas:       Choreograph simple flows; orchestrate complex ones for timeouts/compensations/visibility.
Search:      Inverted index + BM25 + recency; indexing lag is an explicit SLA.
Feeds:       Hybrid feed: fan-out for most, fan-in for celebs; cache the feed head.
Geo:         Index by H3; expand ring cells then Haversine-filter for precision.
Media:       HLS/DASH on CDN with origin shield; versioned URLs and signed access.
Payments:    Idempotency keys + immutable double-entry ledger + nightly reconciliation.
Features:    One feature definition for training/serving; online KV with freshness SLAs; safe rollouts.`}
            </CodeBlock>

            <ExampleBlock title={`${homeFeed.name} — 10-second wrap-up`}>
{`“For the Home Feed, we write events, build projections for timelines and search, hydrate in batches, rank with a feature store, and serve media via CDN—using hybrid fan-out/fan-in to handle celebrities and viral spikes safely.”`}
            </ExampleBlock>
          </Section>

          {/* Optional in-article ad near bottom */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot
                enabled={ADS_ENABLED}
                adSlot="5201219465"
                style={{ display: "block", minHeight: 90, textAlign: "center" }}
              />
            </div>
          )}
        </main>

        {/* RIGHT: Sponsored */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
